/*
 * AppPreviewMain.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useRef } from "react";
import { ViewPreview } from "../../../../ui/components/ViewPreview";
import { IResizeObserverProps, ResizeObserver } from "../../../../ui/components/ResizeObserver";

import { IRenderContext, IViewportConfig } from "@adapptio/lib-core";
import { BEM, EditContext, EditDnD, Viewport } from "@adapptio/lib-components";
import { defaultScreenSizeOption, TScreenSizeId } from "../appPreviewScreenSizes";
import {
	getPreviewResolutionById,
	isBaseScreenResolutionId,
	usePreviewResolutionContext
} from "../../../../previewResolution";
import { ViewEditSelection } from "../../../ViewEditor/Common/ViewEditSelection";
import { isNil } from "@adapptio/lib-shared";

export interface IAppPreviewMainProps {
	className?: string;
	renderContext: IRenderContext;
	previewSize?: TScreenSizeId;
}

export const AppPreviewMain: React.FC<IAppPreviewMainProps> = ({ className, renderContext, previewSize }) => {
	const baseClass = BEM("edt-app-preview", "main", { "screen-size": previewSize });
	const viewElRef = useRef<HTMLDivElement>();

	const dummyDnd = useMemo(() => new EditDnD(), []);
	const dummySelection = useMemo(() => new ViewEditSelection(), []);

	const previewResolutionCtx = usePreviewResolutionContext();
	const { mediaResolutionId, manualWidth, previewResolutionId } = previewResolutionCtx;

	const viewportConfig = useMemo<IViewportConfig>(() => {
		const selectedResolution = getPreviewResolutionById(mediaResolutionId);
		const defaultWidth = isNil(selectedResolution?.screenDefault)
			? "100%"
			: `${selectedResolution.screenDefault}px`;

		const defaultConfig: IViewportConfig = {
			width: isNil(manualWidth) ? defaultWidth : `${manualWidth}px`,
			height: "100%",
			zoom: 1,
			showBoundaries: false,
			showSafeArea: false
		};

		const minWidth = isBaseScreenResolutionId(previewResolutionId)
			? "800px"
			: `${selectedResolution.screenMin || 0}px`;
		const maxWidth = isNil(selectedResolution.screenMax) ? "unset" : `${selectedResolution.screenMax}px`;

		return {
			...defaultConfig,
			minWidth,
			maxWidth
		};
	}, [ mediaResolutionId, manualWidth, previewResolutionId ]);

	const handlePreviewResize = useCallback<IResizeObserverProps["onResize"]>((entry) => {
		previewResolutionCtx.setPreviewWidth(entry.target.clientWidth);
		window?.dispatchEvent(new CustomEvent(Viewport.UPDATE_EVENT_TYPE));
	}, []);

	if (className) {
		baseClass.add(className);
	}

	return (
		<EditContext.Provider
			value={{
				selection: dummySelection,
				componentDnD: dummyDnd,
				dCtx: null,
				media: mediaResolutionId
			}}
		>
			<div className={baseClass.toClassName()}>
				<div className={baseClass.element("wrapper").toClassName()}>
					<ResizeObserver
						onResize={() => window?.dispatchEvent(new CustomEvent(Viewport.RESIZE_EVENT_TYPE))}
					>
						<ViewPreview
							ref={viewElRef}
							onResize={handlePreviewResize}
							renderContext={renderContext}
							viewportConfig={viewportConfig}
							className={baseClass.element("view").toClassName()}
						/>
					</ResizeObserver>
				</div>
			</div>
		</EditContext.Provider>
	);
};

AppPreviewMain.defaultProps = {
	previewSize: defaultScreenSizeOption.id
};
